import { useMemo } from 'react';
import type { Edge, Node } from '@xyflow/react';
import { NODE_COLORS, NODE_DIMENSIONS, type MessageNodeData } from './MessageNode';
import type { ConversationNode } from './types';

const NODE_WIDTH = NODE_DIMENSIONS.width + 32;
const LEVEL_HEIGHT = 100;

interface TraversalItem {
  nodeId: string;
  level: number;
  parentX: number;
  siblingIndex: number;
  siblingCount: number;
}

interface LayoutResult {
  nodes: Node<MessageNodeData>[];
  edges: Edge[];
}

function calculateNodeX(
  parentX: number,
  siblingIndex: number,
  siblingCount: number,
  levelOffset: number,
): number {
  const siblingWidth = siblingCount * NODE_WIDTH;
  const startX = parentX - siblingWidth / 2 + NODE_WIDTH / 2;
  const idealX = startX + siblingIndex * NODE_WIDTH;
  return Math.max(idealX, levelOffset);
}

function createFlowNode(
  nodeId: string,
  node: ConversationNode,
  x: number,
  y: number,
): Node<MessageNodeData> {
  return {
    id: nodeId,
    type: 'message',
    position: { x, y },
    data: {
      uuid: node.uuid,
      fullText: node.fullText,
      preview: node.preview,
      tooltipPreview: node.tooltipPreview,
      sender: node.sender,
      isOnActivePath: node.isOnActivePath,
    },
  };
}

function createFlowEdge(
  nodeId: string,
  node: ConversationNode,
  parentNode: ConversationNode | undefined,
): Edge {
  const isActive = parentNode?.isOnActivePath && node.isOnActivePath;
  return {
    id: `${node.parentUuid}-${nodeId}`,
    source: node.parentUuid!,
    target: nodeId,
    type: 'smoothstep',
    style: {
      stroke: isActive ? NODE_COLORS.edge.active : NODE_COLORS.edge.inactive,
      strokeWidth: 1.5,
    },
  };
}

function computeNodePositions(
  nodesMap: Map<string, ConversationNode>,
  rootNodeId: string,
): Node<MessageNodeData>[] {
  const levelOffsets = new Map<number, number>();
  const result: Node<MessageNodeData>[] = [];
  const queue: TraversalItem[] = [
    {
      nodeId: rootNodeId,
      level: 0,
      parentX: 0,
      siblingIndex: 0,
      siblingCount: 1,
    },
  ];

  while (queue.length > 0) {
    const item = queue.shift();
    if (!item) {
      continue;
    }

    const node = nodesMap.get(item.nodeId);
    if (!node) {
      continue;
    }

    const x = calculateNodeX(
      item.parentX,
      item.siblingIndex,
      item.siblingCount,
      levelOffsets.get(item.level) ?? 0,
    );
    const y = item.level * LEVEL_HEIGHT;

    levelOffsets.set(item.level, x + NODE_WIDTH);
    result.push(createFlowNode(item.nodeId, node, x, y));

    node.children.forEach((childId, index) => {
      queue.push({
        nodeId: childId,
        level: item.level + 1,
        parentX: x,
        siblingIndex: index,
        siblingCount: node.children.length,
      });
    });
  }

  return result;
}

function computeEdges(nodesMap: Map<string, ConversationNode>): Edge[] {
  return Array.from(nodesMap.entries())
    .filter(([, node]) => node.parentUuid)
    .map(([nodeId, node]) => createFlowEdge(nodeId, node, nodesMap.get(node.parentUuid!)));
}

export function useTreeLayout(
  nodesMap: Map<string, ConversationNode>,
  rootNodeId: string | null,
): LayoutResult {
  return useMemo(() => {
    if (!rootNodeId || nodesMap.size === 0) {
      return { nodes: [], edges: [] };
    }

    return {
      nodes: computeNodePositions(nodesMap, rootNodeId),
      edges: computeEdges(nodesMap),
    };
  }, [nodesMap, rootNodeId]);
}
